class WebRTCService {
  constructor(socket, audioMixer) {
    this.socket = socket;
    this.audioMixer = audioMixer;
    this.peerConnections = new Map();
    this.localStream = null;
    
    this.configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ]
    };
    
    this.setupSocketListeners();
  }

  setupSocketListeners() {
    this.socket.on('webrtc-offer', async ({ fromId, offer }) => {
      await this.handleOffer(fromId, offer);
    });
    
    this.socket.on('webrtc-answer', async ({ fromId, answer }) => {
      await this.handleAnswer(fromId, answer);
    });
    
    this.socket.on('webrtc-ice-candidate', async ({ fromId, candidate }) => {
      await this.handleIceCandidate(fromId, candidate);
    });
    
    this.socket.on('participant-left', ({ participantId }) => {
      this.closeConnection(participantId);
    });
  }

  async setLocalStream(stream) {
    this.localStream = stream;
  }

  async createConnection(participantId, roomId) {
    if (this.peerConnections.has(participantId)) {
      return this.peerConnections.get(participantId);
    }
    
    const peerConnection = new RTCPeerConnection(this.configuration);
    
    // Ajouter les tracks locaux
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        peerConnection.addTrack(track, this.localStream);
      });
    }
    
    // Gérer les tracks distants
    peerConnection.ontrack = (event) => {
      console.log(`📡 Track reçu de ${participantId}`);
      const remoteStream = event.streams[0];
      
      // Ajouter le stream distant au mixer audio
      if (this.audioMixer) {
        this.audioMixer.addParticipantStream(participantId, remoteStream);
      }
    };
    
    // Gérer les ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.socket.emit('webrtc-ice-candidate', {
          roomId,
          targetId: participantId,
          candidate: event.candidate
        });
      }
    };
    
    // Gérer l'état de la connexion
    peerConnection.onconnectionstatechange = () => {
      console.log(`État de connexion avec ${participantId}: ${peerConnection.connectionState}`);
      
      if (peerConnection.connectionState === 'failed' || 
          peerConnection.connectionState === 'disconnected' ||
          peerConnection.connectionState === 'closed') {
        this.closeConnection(participantId);
      }
    };
    
    this.peerConnections.set(participantId, peerConnection);
    return peerConnection;
  }

  async createOffer(participantId, roomId) {
    try {
      const peerConnection = await this.createConnection(participantId, roomId);
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);
      
      this.socket.emit('webrtc-offer', {
        roomId,
        targetId: participantId,
        offer
      });
      
      console.log(`📤 Offer envoyée à ${participantId}`);
    } catch (error) {
      console.error(`Erreur lors de la création de l'offer pour ${participantId}:`, error);
    }
  }

  async handleOffer(fromId, offer) {
    try {
      const peerConnection = await this.createConnection(fromId, this.currentRoomId);
      await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
      
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);
      
      this.socket.emit('webrtc-answer', {
        roomId: this.currentRoomId,
        targetId: fromId,
        answer
      });
      
      console.log(`📥 Offer reçue et answer envoyée à ${fromId}`);
    } catch (error) {
      console.error(`Erreur lors du traitement de l'offer de ${fromId}:`, error);
    }
  }

  async handleAnswer(fromId, answer) {
    try {
      const peerConnection = this.peerConnections.get(fromId);
      if (peerConnection) {
        await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
        console.log(`✅ Answer reçue de ${fromId}`);
      }
    } catch (error) {
      console.error(`Erreur lors du traitement de l'answer de ${fromId}:`, error);
    }
  }

  async handleIceCandidate(fromId, candidate) {
    try {
      const peerConnection = this.peerConnections.get(fromId);
      if (peerConnection) {
        await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      }
    } catch (error) {
      console.error(`Erreur lors de l'ajout du ICE candidate de ${fromId}:`, error);
    }
  }

  closeConnection(participantId) {
    const peerConnection = this.peerConnections.get(participantId);
    if (peerConnection) {
      peerConnection.close();
      this.peerConnections.delete(participantId);
      
      // Retirer le stream du mixer
      if (this.audioMixer) {
        this.audioMixer.removeParticipantStream(participantId);
      }
      
      console.log(`❌ Connexion fermée avec ${participantId}`);
    }
  }

  closeAllConnections() {
    this.peerConnections.forEach((_, participantId) => {
      this.closeConnection(participantId);
    });
  }

  setCurrentRoomId(roomId) {
    this.currentRoomId = roomId;
  }
}

export default WebRTCService;
